import queryKeys from '@/constants/queryKeys';
import { publishContent, unpublishContent } from '@/services/api/requests/content';
import { TCustomAxiosError } from '@/services/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useToast from '../useToast';

// Publish + unpublish for a single content item. Both invalidate the detail and list
// queries and surface success/error toasts.
const usePublishContent = (id: string, onSettled?: () => void) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: [queryKeys.content.byId, id] });
    await queryClient.invalidateQueries({ queryKey: [queryKeys.content.all] });
  };

  const publish = useMutation({
    mutationFn: async () => publishContent(id),
    onSuccess: async () => {
      await invalidate();
      toast({ status: 'success', title: 'Success', description: 'Content published.' });
      onSettled?.();
    },
    onError: async (error: TCustomAxiosError) => {
      await invalidate();
      toast({ status: 'error', title: 'Error publishing content', description: error.errors?.[0]?.message });
    },
  });

  const unpublish = useMutation({
    mutationFn: async () => unpublishContent(id),
    onSuccess: async () => {
      await invalidate();
      toast({ status: 'success', title: 'Success', description: 'Content unpublished.' });
      onSettled?.();
    },
    onError: async (error: TCustomAxiosError) => {
      await invalidate();
      toast({ status: 'error', title: 'Error unpublishing content', description: error.errors?.[0]?.message });
    },
  });

  return {
    publish: publish.mutate,
    unpublish: unpublish.mutate,
    isPublishing: publish.isPending,
    isUnpublishing: unpublish.isPending,
  };
};

export default usePublishContent;
